import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useModals } from '@/feature/alerts/hooks/use-modals';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useBetslipActions } from '@/feature/betslip-pickem/hooks/use-betslip-actions';
import { useBetslipNavigation } from '@/feature/betslip-pickem/hooks/use-betslip-navigation';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { getCanAddPickOfType, getMaxAllowedPicksForType } from '@/feature/betslip-pickem/hooks/use-entry-rules';
import { BetslipPick, PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { isPlayerSelected } from '@/feature/betslip-pickem/utils/betslip-utils';
import { getPickTypeLabel } from '@/feature/betslip-pickem/utils/game-mode-utils';
import { ProjectionType } from '@/types/api.generated';

import { checkIfPromoProjection } from '../utils/lineupsUtils';

export type PickSelectionMethods = {
    removeSelection: (
        listToRemove: {
            eventId: string;
            player: PlayerWithTeam;
        }[]
    ) => void;
    makeSelection: (pick: BetslipPick, analyticsTag?: string) => boolean;
};
/**
 * This hook exposes 3 methods:
 * removeSelection: removes a player from the PickSlip
 * makeSelection: adds a player in the PickSlip if the following conditions are true:
 *          BOOSTED (rocket): boosted players limit was not reached;
 *                   we replace the same player's boosted proj with another of his boosted projections
 *                   players limit can be reached, but boosted limit wasn't, and we choose a boosted proj to replace a regular projection of an already selected player
 *         SPECIAL:  special players limit was not reached;
 *                   we replace the same player's special proj with another of his special projections
 *                   players limit can be reached, but special limit wasn't, and we choose a special proj to replace a regular projection of an already selected player
 *         REGULAR: player limit is not reached. But if the player is already in the pickslip(for the same event), we allow selection because we only change his projection.
 *                  if the selection can not cause the same player to be added twice (even if the selections are in different events)
 */
export const usePickSelection = (): PickSelectionMethods => {
    const { removeSelection: remove, addSelection } = useBetslipActions();
    const removeAllPicks = useBetslipStore(state => state.actions.clearBetslip);

    const { showInfoSheet } = useAlerts();
    const { navigateToPickslip } = useBetslipNavigation();
    const { t } = useTranslation('betslip_pickem');
    const { dismissAll } = useModals();

    const showInfoModal = useCallback(
        (title: string, description: string, showExtraAction?: boolean) => {
            const infoSheetProps = {
                title: title,
                description: description,
                buttonLabel: 'Edit lineup',
                primaryButtonTestId: 'edit-lineup-button',
                handlePress: () => {
                    navigateToPickslip(false, true);
                    dismissAll();
                },
                secondaryLabel: 'Dismiss',
                secondaryButtonTestId: 'dismiss-button',
            };

            if (showExtraAction) {
                showInfoSheet({
                    ...infoSheetProps,
                    deleteButtonLabel: 'Clear lineup',
                    deleteButtonActionPress: () => {
                        removeAllPicks();
                        dismissAll();
                    },
                });
            } else {
                showInfoSheet(infoSheetProps);
            }
        },
        [navigateToPickslip, dismissAll, showInfoSheet, removeAllPicks]
    );

    const removeSelection = useCallback(
        (listToRemove: { eventId: string; player: PlayerWithTeam }[]) => {
            remove(listToRemove);
            listToRemove.forEach(pickToRemove => {
                BetrAnalytics.trackEvent(AnalyticsEvent.REMOVE_PICK, {
                    pickId: pickToRemove.player.id,
                });
            });
        },
        [remove]
    );

    const makeSelection = useCallback(
        (pick: BetslipPick, analyticsTag?: string): boolean => {
            // Read betslip imperatively to avoid subscribing to store changes.
            // Previously, betslip was in the deps array, causing makeSelection to get a new reference
            // on every pick, which cascaded re-renders through the entire lobby FlatList.
            const currentBetslip = useBetslipStore.getState().betslip;
            const playerPickedEntry = isPlayerSelected({ eventId: pick.eventId, playerId: pick.player.id });
            const canAddPick = getCanAddPickOfType(pick.projection.type);
            const isReplacingSameProjectionType = playerPickedEntry?.projection.type === pick.projection.type;
            const canAddMorePicksOfType =
                currentBetslip.filter(it => it.projection.type === pick.projection.type).length <
                getMaxAllowedPicksForType(pick.projection.type);

            if (!canAddPick) {
                //Pick can not be added, check why
                if (playerPickedEntry && isReplacingSameProjectionType) {
                    //Alow replacing the same projection type for a selected player
                    addSelection(pick);
                    return true;
                }
                //Adding promo pick
                if (checkIfPromoProjection(pick.projection.type)) {
                    if (isChangingSpecialProjections(pick, playerPickedEntry)) {
                        //Allow changing special projections for a selected player
                        addSelection(pick);
                        return true;
                    }
                    //If we are trying to add another special pick that can not be added, show the message that only one of
                    //discount, surge, anchor, nuke or free pick can be added to the same lineup
                    showInfoModal(
                        t('maxed_special_picks'),
                        'Only one special pick per lineup is allowed. Special Picks include Discount, Surge, Anchor, Nuke and Free Pick.',
                        true
                    );
                    return false;
                }

                if (
                    playerPickedEntry &&
                    canAddMorePicksOfType &&
                    isDowngradingMultiplier(pick.projection.type, playerPickedEntry?.projection.type)
                ) {
                    //Allow downgrading a projection for the picked player
                    addSelection(pick);
                    return true;
                }

                if (!canAddMorePicksOfType) {
                    //Reached the max number of EDGE, BOOSTED etc
                    const pickTypeLabel = getPickTypeLabel(pick.projection.type);
                    showInfoModal(t('maxed_picks'), t('maxed_special_description', { type: pickTypeLabel }), true);
                    return false;
                }

                showInfoModal(t('maxed_payout'), t('maxed_payout_description'), true);
                return false;
            }

            if (analyticsTag) {
                BetrAnalytics.trackEvent(AnalyticsEvent.NEW_PICK, {
                    location: analyticsTag,
                    projection: pick.projection.name,
                    projectionValue: pick.projection.value,
                    outcome: pick.outcome,
                    pickId: pick.player.id,
                });
            }

            addSelection(pick);
            return true;
        },
        [addSelection, showInfoModal, t]
    );

    return {
        makeSelection,
        removeSelection,
    };
};

const isChangingSpecialProjections = (newPick: BetslipPick, currentPick?: BetslipPick) => {
    if (!currentPick) {
        return false;
    }
    return checkIfPromoProjection(newPick.projection.type) && checkIfPromoProjection(currentPick.projection.type);
};

const ProjectionsByScoring = [
    ProjectionType.Nuke,
    ProjectionType.Boosted4,
    ProjectionType.SuperBoosted,
    ProjectionType.Boosted,
    ProjectionType.MiniBoosted,
    ProjectionType.Regular,
    ProjectionType.Special,
    ProjectionType.FreePick,
    ProjectionType.Edge1,
    ProjectionType.Edge2,
    ProjectionType.Edge3,
    ProjectionType.Edge4,
    ProjectionType.Anchor,
];

const isDowngradingMultiplier = (newProjection: ProjectionType, currentProjection: ProjectionType) => {
    return ProjectionsByScoring.indexOf(newProjection) > ProjectionsByScoring.indexOf(currentProjection);
};
